import React, { useContext, useEffect } from 'react';
import {
  Card,
  Column,
  GridContainer,
  Li,
  Row,
  Typography,
  Ul,
  useScreenClass,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import useIdentityContext from '../../../hooks/useIdentityContext';
import useSiteData from '../../../hooks/useSiteData';
import { DESIGN_FEEDBACK_REQUEST_GROUP_PAGE } from '../../../constants/orderdetailsui';
import { AutoResolutionType } from '../../../types/feedback/Feedback';
import DesignFeedbackGroupRequestContext from '../../contexts/DesignFeedbackGroupRequestContext';
import { trackDesignFeedbackPageViewed } from '../../../apis/newRelic/designFeedbackPageNewRelicTracking';
import ReviewDesignsForGroup from './ReviewDesignsForGroup';
import { EmbeddableLinkCreator } from '../../shared/EmbeddableLinkCreator';
import { MemoizedItemPreviewDisplay } from '../../shared/itemPreviewDisplay/ItemPreviewDisplay';
import { ExpertCard } from './ExpertCard';
import { RequestPageHeader } from './RequestPageHeader';

const DesignFeedbackGroupRequestPage = () => {
  const screenClass = useScreenClass();
  const isMobile = ['xs'].includes(screenClass);

  const [designFeedbackMessages, lineItemMessages] = useTranslations([
    'designFeedbackMessages',
    'lineItemMessages',
  ]);

  const { vistacartTenant } = useSiteData();

  const { identity } = useIdentityContext();
  const { shopperId } = identity;

  const {
    groupId,
    orderId,
    orderPlacedAnonymously,
    feedbackGroupState,
    autoResolutionType,
    reasons,
    totalQuantity,
    lineItems,
    commonProductName,
    productName,
    productKey,
    productVersion,
    livePreviewUrl,
    documentRefUrl,
    productAttributeList,
  } = useContext(DesignFeedbackGroupRequestContext);

  useEffect(() => {
    const trackingData = {
      groupId,
      orderNumber: orderId,
      feedbackGroupState,
      orderPlacedAnonymously,
      tenant: vistacartTenant,
    };

    trackDesignFeedbackPageViewed(
      shopperId,
      DESIGN_FEEDBACK_REQUEST_GROUP_PAGE,
      trackingData
    );
  }, [
    groupId,
    orderId,
    orderPlacedAnonymously,
    shopperId,
    feedbackGroupState,
    vistacartTenant,
  ]);

  let deadlineAutoActionDisclaimer;
  switch (autoResolutionType) {
    case AutoResolutionType.AUTO_APPROVAL:
      deadlineAutoActionDisclaimer = designFeedbackMessages.deadlineAutoApprove;
      break;
    case AutoResolutionType.AUTO_CANCEL:
      deadlineAutoActionDisclaimer = designFeedbackMessages.deadlineAutoCancel;
      break;
    default:
      deadlineAutoActionDisclaimer = designFeedbackMessages.deadlineAutoApprove;
  }

  const { embroideryUrl } = useSiteData();
  const EmbroideryGuidelinesLink = EmbeddableLinkCreator({
    href: embroideryUrl,
    tabIndex: 0,
  });

  const defaultReasons = [
    <ReplaceTokens
      tokenizedString={designFeedbackMessages.embroiderySimplification}
      substitutions={{
        EmbroideryGuidelinesLink,
      }}
    />,
  ];
  const reasonsInExpertCard = reasons?.length > 0 ? reasons : defaultReasons;

  const itemList = (
    <Typography mb={3}>
      <Ul>
        {lineItems.map((item, index) => (
          <Li pb={0} key={`item-${index}`}>
            {item.productName} ({item.quantity})
          </Li>
        ))}
      </Ul>
    </Typography>
  );

  const itemCard = (
    <Card bordered mb={6}>
      <GridContainer>
        <Row>
          <Column span={2} spanSm={4} spanXs={4}>
            <MemoizedItemPreviewDisplay
              productAttributeList={productAttributeList}
              documentRefUrl={documentRefUrl}
              livePreviewUrl={livePreviewUrl}
              productKey={productKey}
              productVersion={productVersion}
              productName={productName}
              hideMagnifyingGlass
            />
          </Column>
          <Column span={10} spanSm={8} spanXs={8}>
            <Typography fontSkin="body-standard-bold" mb={3}>
              {commonProductName}
            </Typography>
            <Typography mb={3}>
              {lineItemMessages.quantityField}: {totalQuantity}
            </Typography>
            {isMobile ? null : itemList}
          </Column>
        </Row>
        {isMobile ? (
          <Row>
            <Column span={12}>{itemList}</Column>
          </Row>
        ) : null}
      </GridContainer>
    </Card>
  );

  return (
    <GridContainer>
      <Row>
        {isMobile ? (
          <Column span={6} mb={9}>
            <RequestPageHeader />
            <br />
            <ReviewDesignsForGroup />
          </Column>
        ) : null}
        <Column span={6}>
          {isMobile ? null : <RequestPageHeader />}
          <Typography mt={6} mb={6}>
            {designFeedbackMessages.reviewAndRefineMessage}
          </Typography>
          {itemCard}
          <ExpertCard
            title={designFeedbackMessages.expertEdits}
            messages={reasonsInExpertCard}
          />
          <Typography mt={6}>{deadlineAutoActionDisclaimer}</Typography>
        </Column>

        {isMobile ? null : (
          <Column span={5} offset={1}>
            <ReviewDesignsForGroup />
          </Column>
        )}
      </Row>
    </GridContainer>
  );
};

export default DesignFeedbackGroupRequestPage;
